import { PAGE_TYPE_CT_CART } from '@modules/app'
import { CHECKOUT_METHOD_SPOONFLOWER } from '@modules/checkout'
import { useComposable } from '@modules/oriuminc/base'
import { getUserAnalytics } from '@spoonflower/user-analytics'
import { PATHS } from 'constants/paths'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { useEventListener } from 'usehooks-ts'
import useCommerce from '../../../../app/hooks/useCommerce'
import useIdentity from '../../../../app/hooks/useIdentity'
import useUserStats from '../../../../app/hooks/useUserStats'
import { useCart, useProductBySlug } from '../../hooks'
import {
  gaTrackBeginCheckout,
  gaTrackClearEcommerce,
  gaTrackExposure,
  gaTrackPageType,
  gaTrackPageView,
  gaTrackUserInformation,
  gaTrackViewCart,
  gaTrackViewItem,
  SplitTest,
  trackCartViewed,
  trackCheckoutStarted,
} from '../../integrations'
import { CartFragment } from '../../types'
import { getCartDiscounts } from '../../utils'

export const Analytics = () => {
  const router = useRouter()
  const { path } = useComposable()
  const { cart } = useCart()
  const { lineItemDiscounts } = getCartDiscounts(cart as CartFragment)
  const { data: identity, isFetched } = useIdentity()
  const { data: commerce } = useCommerce()
  const { id: userId } = identity ?? {}
  const { product } = useProductBySlug(`${router.query?.slug}`)
  const { stats, statsFetched } = useUserStats(
    identity?.id as number,
    'en' // If we ever go back to multiple locales, we should get this from preferences`
  )

  // Prevent multiple event firing for View Cart and Begin Checkout
  const [hasViewedCart, setHasViewedCart] = useState(false)
  const [hasBegunCheckout, setHasBegunCheckout] = useState(false)
  // Couldn't user react query for this because of the way the data is fetched
  const [gaStatsFired, setGaStatsFired] = useState(false)
  const trackedCartViewId = useRef<string>()

  //Exposures for split testing
  const [exposures, setExposures] = useState<SplitTest[]>([])
  const exposuresRef = useRef<SplitTest[]>([])

  const handleExpose = (event: Event) => {
    const customEvent = event as CustomEvent
    setExposures((prevExposures) => {
      const alreadyExposed = prevExposures.some(
        (e) => e.id === `experiment_${customEvent.detail.name}`
      )
      if (alreadyExposed) return prevExposures

      return [
        ...prevExposures,
        {
          id: `experiment_${customEvent.detail.name}`,
          group: customEvent.detail.variant,
        },
      ]
    })
  }

  useEventListener('expose', handleExpose)

  useEffect(() => {
    gaTrackPageType(PAGE_TYPE_CT_CART)
  }, [])

  useEffect(() => {
    exposuresRef.current = exposures
  }, [exposures])

  useEffect(() => {
    // When navigating back and forth through checkout steps state does not reset and it needs to be reset to fire view_cart and begin_checkout events again
    setHasViewedCart(false)
  }, [router.pathname])

  useEffect(() => {
    getUserAnalytics({
      url: '/api/spoonflower/get-user-stats',
    }).then((data) => {
      if (data) {
        gaTrackPageView(
          PAGE_TYPE_CT_CART,
          data.data_layer,
          data.cart?.cart_id,
          data.cart?.guest_cart_id,
          exposuresRef.current
        )
      }
    })
  }, [router.asPath])

  useEffect(() => {
    gaTrackExposure(exposures)
  }, [exposures])

  useEffect(() => {
    if (router.asPath !== PATHS.CART) {
      trackedCartViewId.current = undefined
      return
    }

    if (!cart?.id || trackedCartViewId.current === cart.id) {
      return
    }

    trackedCartViewId.current = cart.id
    trackCartViewed({ cart, discounts: lineItemDiscounts })
  }, [router.asPath, cart, lineItemDiscounts])

  useEffect(() => {
    const inCartPage = router.asPath == PATHS.CART
    if (cart?.id && inCartPage && !hasViewedCart && isFetched && gaStatsFired) {
      gaTrackViewCart({ cart, discounts: lineItemDiscounts })
      setHasViewedCart(true)
      setHasBegunCheckout(false)
    }
  }, [router, cart, path, hasViewedCart, userId, isFetched, gaStatsFired])

  // Track Checkout Begin
  useEffect(() => {
    const isCheckoutPage = router.asPath === path.getCheckout()
    if (isCheckoutPage && cart?.id && !hasBegunCheckout) {
      gaTrackClearEcommerce()
      gaTrackBeginCheckout({
        cart,
        discounts: lineItemDiscounts,
        checkoutType: CHECKOUT_METHOD_SPOONFLOWER,
      })
      trackCheckoutStarted({
        cart,
        discounts: lineItemDiscounts,
        checkoutType: CHECKOUT_METHOD_SPOONFLOWER,
      })
      setHasBegunCheckout(true)
    }
  }, [router, cart, path, hasBegunCheckout])

  // Track View Item
  useEffect(() => {
    const isProductPage = router.asPath.startsWith(path.getPDP({ slug: '' }))
    if (isProductPage && product) {
      gaTrackViewItem({ product })
    }
  }, [router, path, product])

  // Track User Information
  useEffect(() => {
    if (stats) {
      gaTrackUserInformation(PAGE_TYPE_CT_CART, stats?.data?.data_layer)
    }
    if (statsFetched) {
      setGaStatsFired(true)
    }
  }, [statsFetched])

  return null
}
